// Core
import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Icon } from "components/ShellV2";

// Utils
import { avColor, initials } from "utils/avatar";

const fmt = (v) =>
  v != null ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "0";

/* Master-detail economic balance per member. Each row expands to show the
   revenue breakdown (the same fields the legacy EconomicBalanceTable showed). */
function BalanceTable({ rows }) {
  const { t } = useTranslation(["settlement"]);
  const [openIds, setOpenIds] = useState(() => new Set());
  const [q, setQ] = useState("");

  const cols = t("analytics.tables.economic_balance.columns", { returnObjects: true });
  const parts = [
    ["rev_sup", cols.rev_sup],
    ["rev_pur", cols.rev_lem_exc_neg],
    ["rev_sale", cols.rev_lem_exc_pos],
    ["rev_sur", cols.rev_sur],
    ["rev_slc_soc", cols.rev_slc],
    ["rev_lem_fee", cols.rev_lem_fee],
    ["rev_pow", cols.rev_pow],
  ];

  const view = q.trim()
    ? rows.filter((r) => (r.member || "").toLowerCase().includes(q.trim().toLowerCase()))
    : rows;

  const toggleRow = (id) => {
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenIds(new Set(rows.map((r) => r.id)));
  const collapseAll = () => setOpenIds(new Set());

  return (
    <div>
      <div className="rec-toolbar">
        <div className="rec-input-wrap max-w-[280px]">
          <Icon name="search" />
          <input
            className="rec-input"
            placeholder={t("analytics.tables.search_member", { defaultValue: "Procurar membro…" })}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="grow" />
        <button type="button" className="rec-btn-ghost rec-btn-sm" onClick={expandAll}>
          <Icon name="unfold_more" />
          {t("analytics.tables.economic_balance.expand_all")}
        </button>
        <button type="button" className="rec-btn-ghost rec-btn-sm" onClick={collapseAll}>
          <Icon name="unfold_less" />
          {t("analytics.tables.economic_balance.collapse_all")}
        </button>
      </div>
      <div className="rec-bal-head">
        <span>{cols.member}</span>
        <span className="rec-bal-num">{cols.total_balance}</span>
        <span className="rec-bal-num">{cols.savings}</span>
        <span className="rec-bal-chev" />
      </div>
      {view.map((r) => {
        const isOpen = openIds.has(r.id);
        return (
          <div key={r.id} className={`rec-bal-item${isOpen ? " open" : ""}`}>
            <button type="button" className="rec-bal-row" onClick={() => toggleRow(r.id)}>
              <div className="rec-cell-member">
                <div className="rec-av-sm" style={{ background: avColor(r.member) }}>
                  {initials(r.member)}
                </div>
                <b>{r.member}</b>
              </div>
              <span className="rec-bal-num" style={{ fontWeight: 700 }}>
                {fmt(r.rev_total_pow)} €
              </span>
              <span className="rec-bal-num" style={{ fontWeight: 700, color: "var(--success)" }}>
                +{fmt(r.savings)} €
              </span>
              <span className="rec-bal-chev">
                <Icon name="expand_more" />
              </span>
            </button>
            {isOpen && (
              <div className="rec-bal-detail">
                {parts.map(([k, label]) => (
                  <div key={k} className="rec-bal-part">
                    <span className="rec-bal-part-l">{label}</span>
                    <span className="rec-bal-part-v">{fmt(r[k])} €</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

BalanceTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      member: PropTypes.string,
      rev_sup: PropTypes.number,
      rev_pur: PropTypes.number,
      rev_sale: PropTypes.number,
      rev_sur: PropTypes.number,
      rev_slc_soc: PropTypes.number,
      rev_lem_fee: PropTypes.number,
      rev_pow: PropTypes.number,
      rev_total_pow: PropTypes.number,
      savings: PropTypes.number,
    })
  ),
};

BalanceTable.defaultProps = {
  rows: [],
};

export default BalanceTable;
